import * as React from 'react';
import type {
  AtomRenderProps,
  EnvironmentContextType,
} from '@packages/systems/wfdl';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {weakMemo} from '@packages/utilities/memo';
import type {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {
  createEnclosingRichText,
  getEnclosingRichTextKey,
  getRichTextOverlayCandidates,
  type EnclosingRichText,
} from '@packages/systems/overlays';

type Context = AtomRenderProps['context'];

const ENCLOSING_RICH_TEXT = 'enclosingRichText';

const getChildElements = (children: readonly unknown[]): ExpressionElement[] =>
  children.flatMap((child) =>
    React.isValidElement<{element?: ExpressionElement}>(child) &&
    child.props.element
      ? [child.props.element]
      : []
  );

/**
 * Returns the overlay targets of a rich text element for its descendants to
 * read while rendering. The scope is rebuilt only when its targets or the rich
 * text's position change, so editing the rich text doesn't re-render
 * unchanged children.
 */
export const useEnclosingRichText = (
  children: readonly unknown[],
  nativeIdPath: readonly NativeId[],
  environmentContext: EnvironmentContextType
): EnclosingRichText => {
  const candidates = getRichTextOverlayCandidates(getChildElements(children));
  const key = getEnclosingRichTextKey(
    nativeIdPath,
    environmentContext,
    candidates
  );

  return React.useMemo(() => createEnclosingRichText(key), [key]);
};

// Only the nearest rich text is kept: a rich text nested inside another one
// (e.g. in a component placed in CMS rich text) replaces the outer one for its
// descendants.
export const getEnclosingRichText = (
  context: Context
): EnclosingRichText | undefined => context.get(ENCLOSING_RICH_TEXT);

export const withEnclosingRichText = weakMemo((context: Context) =>
  weakMemo((content: EnclosingRichText) =>
    context.set(ENCLOSING_RICH_TEXT, content)
  )
);
